import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import { addResourceToProject, checkResourceAccess } from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import AccessReviewDialog from './AccessReviewDialog';
import {
  PiMagnifyingGlassLight,
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiTableLight,
  PiFileMagnifyingGlassLight,
  PiBookOpenTextLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiCheckCircleLight,
  PiLinkLight,
} from 'react-icons/pi';
import debounce from 'lodash.debounce';
import Input from '../shared/TailwindComponents/Input';

// ─── Resource types available for linking ───────────────────────

const RESOURCE_TYPES: { key: string; label: string; icon: React.ReactElement; color: BadgeColor; endpoint: string }[] = [
  { key: 'matter', label: 'Matters', icon: <PiBriefcaseLight className="w-4 h-4" />, color: 'blue', endpoint: '/matters/view' },
  { key: 'task', label: 'Tasks', icon: <PiCheckSquareOffsetLight className="w-4 h-4" />, color: 'green', endpoint: '/tasks/list' },
  { key: 'table', label: 'Tables', icon: <PiTableLight className="w-4 h-4" />, color: 'teal', endpoint: '/tables' },
  { key: 'review_session', label: 'Reviews', icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />, color: 'sky', endpoint: '/reviews' },
  { key: 'playbook', label: 'Playbooks', icon: <PiBookOpenTextLight className="w-4 h-4" />, color: 'emerald', endpoint: '/playbooks' },
  { key: 'datasource', label: 'Data Sources', icon: <PiDatabaseLight className="w-4 h-4" />, color: 'cyan', endpoint: '/datasources' },
  { key: 'template', label: 'Templates', icon: <PiFileTextLight className="w-4 h-4" />, color: 'slate', endpoint: '/templates/structure/list' },
];

interface ProjectResourcesPanelProps {
  projectUuid: string;
  onClose: () => void;
  onResourceLinked?: () => void;
}

const ProjectResourcesPanel: React.FC<ProjectResourcesPanelProps> = ({ projectUuid, onClose, onResourceLinked }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState<string>('matter');
  const [searchTerm, setSearchTerm] = useState('');
  const [resources, setResources] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());

  // Fetch already-linked resource UUIDs on mount
  useEffect(() => {
    const fetchLinked = async () => {
      try {
        const response = await apis.get(`/projects/${projectUuid}/resources`);
        const data = response.data;
        const uuids = new Set<string>();
        // Use the flat linked_resource_uuids list (includes ALL linked UUIDs
        // regardless of the caller's access level) so the link panel correctly
        // shows "Linked" for every already-linked resource.
        if (data?.linked_resource_uuids) {
          for (const uuid of data.linked_resource_uuids) {
            uuids.add(uuid);
          }
        } else if (data?.resources) {
          // Fallback for older backend versions
          for (const items of Object.values(data.resources) as any[][]) {
            for (const item of items) {
              if (item.resource_uuid) uuids.add(item.resource_uuid);
            }
          }
        }
        setLinkedIds(uuids);
      } catch (error) {
        // Non-critical - panel still works, just won't show pre-linked state
      }
    };
    fetchLinked();
  }, [projectUuid]);

  // Fetch resources when type or search changes.
  // Uses AbortController to cancel stale requests - when the user toggles
  // tabs quickly, an older response can resolve after a newer one and
  // overwrite the list with the wrong resource type.
  useEffect(() => {
    const abortController = new AbortController();

    const fetchResources = async () => {
      const typeConfig = RESOURCE_TYPES.find((t) => t.key === selectedType);
      if (!typeConfig) return;

      setIsLoading(true);
      try {
        const params: any = { page: 1, page_size: 50 };
        if (searchTerm) params.search_term = searchTerm;

        const response = await apis.get(typeConfig.endpoint, {
          params,
          signal: abortController.signal,
        });

        // If this request was aborted while in flight, don't update state
        if (abortController.signal.aborted) return;

        let items: any[] = [];
        const data = response.data;
        if (data.items) items = data.items;
        else if (data.matters) items = data.matters;
        else if (data.tasks) items = data.tasks;
        else if (data.tables) items = data.tables;
        else if (data.reviews) items = data.reviews;
        else if (data.playbooks) items = data.playbooks;
        else if (data.datasources) items = data.datasources;
        else if (data.templates) items = data.templates;
        else if (Array.isArray(data)) items = data;
        else if (data.data && Array.isArray(data.data)) items = data.data;

        // Playbooks: hide PONS-curated / global ones — only the org's own
        // playbooks are linkable to a project.
        if (selectedType === 'playbook') {
          items = items.filter(
            (it: any) =>
              it?.is_global !== true &&
              !String(it?.uuid || '').toLowerCase().startsWith('pons-pb-')
          );
        }

        setResources(items);
      } catch (error: any) {
        // Aborted requests throw - don't clear the list for those
        if (error?.name === 'CanceledError' || abortController.signal.aborted) return;
        setResources([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchResources();

    // Cleanup: abort the in-flight request when selectedType or searchTerm changes
    return () => abortController.abort();
  }, [selectedType, searchTerm]);

  const handleLink = async (resourceUuid: string) => {
    setLinkingId(resourceUuid);
    const result = await dispatch(addResourceToProject(projectUuid, selectedType, resourceUuid));
    setLinkingId(null);
    if (!result) return;

    setLinkedIds((prev) => new Set(prev).add(resourceUuid));
    if (onResourceLinked) onResourceLinked();

    // Backend now returns `missing_access_users` directly in the link response
    // (see project_service.add_resource). If the list is non-empty, surface
    // the access-review dialog so the linker sees the asymmetric consequence
    // of their action - matches the "attach \u2260 share" legal default from
    // sharded-frolicking-lake.md.
    const missingUsers: any[] = Array.isArray(result.missing_access_users) ? result.missing_access_users : [];
    if (missingUsers.length === 0) return;

    // The dialog still calls /access-check for `can_grant_access`, but we pass
    // the user list from the link response for immediate rendering. The
    // separate checkResourceAccess call remains for the authoritative
    // can_grant_access flag (admin vs non-admin linker).
    const accessResult = await dispatch(checkResourceAccess(projectUuid, selectedType, resourceUuid));
    if (!accessResult || !accessResult.collaborators?.length) return;

    const typeConfig = RESOURCE_TYPES.find((t) => t.key === selectedType);
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Review Access'),
      width: '480px',
      content: (
        <AccessReviewDialog
          projectUuid={projectUuid}
          resourceType={selectedType}
          resourceUuid={resourceUuid}
          resourceLabel={typeConfig?.label?.replace(/s$/, '').toLowerCase() || selectedType}
          collaborators={accessResult.collaborators}
          canGrantAccess={accessResult.can_grant_access ?? false}
          onClose={() => dispatch(hideModal())}
          onAccessGranted={() => { if (onResourceLinked) onResourceLinked(); }}
        />
      ),
      onCancel: () => {},
    }));
  };

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 300),
    []
  );

  const getResourceTitle = (resource: any) =>
    resource.title ||
    resource.name ||
    resource.label ||
    // Templates list uses PascalCase: TemplateName / Structure.title.
    resource.TemplateName ||
    resource?.Structure?.title ||
    resource.original_filename ||
    'Untitled';
  const getResourceUuid = (resource: any) => resource.uuid || resource.id;
  const getResourceStatus = (resource: any) => resource.status || resource.processing_status;

  const activeTypeConfig = RESOURCE_TYPES.find((t) => t.key === selectedType);

  return (
    <div className="flex flex-col h-full">
      {/* Type selector pills */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 tracking-wide">{t('Resource Type')}</p>
        <div className="flex flex-wrap gap-1.5">
          {RESOURCE_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => {
                setSelectedType(type.key);
                setSearchTerm('');
                setResources([]);
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md transition-all ${
                selectedType === type.key
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 font-medium ring-1 ring-primary-200 dark:ring-primary-700'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {type.icon}
              {t(type.label)}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700/60">
        <div className="relative">
          <PiMagnifyingGlassLight style={{ zIndex: '1000000' }} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('Search {{type}}...', { type: activeTypeConfig?.label || '' })}
            onChange={(e) => debouncedSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Resource list */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-main">
        {isLoading ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3.5">
                <div className="flex-1 min-w-0 mr-3 space-y-2">
                  <div className="h-3.5 w-3/5 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
                  <div className="h-2.5 w-2/5 bg-gray-100 dark:bg-gray-700/60 rounded animate-pulseFade" />
                </div>
                <div className="h-7 w-14 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
              </div>
            ))}
          </div>
        ) : resources.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-500">
            {activeTypeConfig?.icon && React.cloneElement(activeTypeConfig.icon, { className: 'w-8 h-8 mb-2 opacity-40' })}
            <p className="text-sm">{searchTerm ? t('No results found') : t('No {{type}} available', { type: activeTypeConfig?.label || '' })}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {resources.map((resource, index) => {
              const uuid = getResourceUuid(resource);
              const title = getResourceTitle(resource);
              const status = getResourceStatus(resource);
              const isLinked = linkedIds.has(uuid);
              const isLinking = linkingId === uuid;

              return (
                <div
                  key={uuid || index}
                  className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <div className="min-w-0 flex-1 mr-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {status && (
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">{status}</span>
                      )}
                    </div>
                  </div>
                  {isLinked ? (
                    <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <PiCheckCircleLight className="w-4 h-4" />
                      <span className="text-xs font-medium">{t('Linked')}</span>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => handleLink(uuid)}
                      disabled={isLinking}
                    >
                      {isLinking ? (
                        <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent" />
                      ) : (
                        <>
                          <PiLinkLight className="w-3.5 h-3.5 mr-1" />
                          {t('Link')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectResourcesPanel;
